import { Box, Button, FormControlLabel, FormGroup, Grid, Menu, MenuItem, Stack, styled, Switch, Typography } from '@mui/material'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from '../../Styles/ComponentStyles/AdminDashboard/AdminDashboard.module.scss'
import company from '../../assets/Images/Dashboard/Agent.svg'
import profiles from '../../assets/Images/Dashboard/profiles.svg'
import activeAgent from '../../assets/Images/Agent/Active Agent.svg'
import pendingReqIcon from '../../assets/Images/Dashboard/pendingReqIcon.svg'
import { DataGrid, GridColDef, GridToolbar, GridToolbarContainer, GridValueGetterParams, GridRowsProp } from '@mui/x-data-grid'
import CustomPagination from '../../GlobalComponent/CustomPagination/CustomPagination'
import agent from '../../service/api'
import { AdminDashboardContext, AdminDashboardContextPayload } from '../../Context/AdminDetailsContext'
import moment from 'moment'
import { AdminDetailsResponse } from '../../Models/axiosModel'
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext'
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import GlobalPopUp from '../../GlobalComponent/GlobalPopup/GlobalPopup'
import RegisterAgent from '../../GlobalComponent/RegisterAgent/RegisterAgent'
import GlobalDeletePopup from '../../GlobalComponent/GlobalDeletePopup/GlobalDeletePopup'
import GlobalTabsComponent from '../../GlobalComponent/GlobalTabsComponent/GlobalTabsComponent'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

// Row shape for the "Recent Agent Logins" grid.
// Adjust field names if your model differs — kept explicit to remove `any`.
interface AgentRow {
    user_id: number
    firstname: string
    lastname: string
    last_login: string | null
    email: string
    phone_number: string
    profile_count: number
    status: string
    user_role: string
}

// Row shape for the "Latest Created Profiles" grid.
interface ProfileRow {
    user_profile_id: number
    user_name: string
    user_profile_type_id: number
    change_log_timestamp: string
    user_email_address: string
    user_contact: string
    sample_profile_flag: boolean
    assignee_fullname: string | null
}

interface AgentFormDetails {
    first_name: string
    last_name: string
    email_address: string
    contact_number: string
}

const EMPTY_FORM: AgentFormDetails = {
    first_name: '',
    last_name: '',
    email_address: '',
    contact_number: '',
}

const PROFILE_TYPE_LABELS: Record<number, string> = {
    1: 'IUL',
    2: 'Annuity-Roth',
    3: 'Annuity-Income',
    4: 'Annuity-Accumulation',
}

/* ------------------------------------------------------------------ */
/* Module-scope components (defined once, not re-created per render)   */
/* ------------------------------------------------------------------ */

// FIX: previously defined inside the component, which remounted it every render.
const IOSSwitch = styled((props: any) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 36,
    height: 20,
    padding: 0,
    margin: '5px !important',
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: '2px !important',
        transitionDuration: '300ms',
        top: '0px',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#3B78CE',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiFormControlLabel-root': {
        marginLeft: '0px !important',
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 15,
        height: 15,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}))

// FIX: moved out of the component body so they aren't recreated each render.
const CustomToolbar = () => (
    <GridToolbarContainer
        sx={{
            margin: '5px 0px 25px',
            float: 'right',
            '& .MuiButtonBase-root': {
                color: '#ffffff !important',
            },
        }}
    >
        <GridToolbar />
    </GridToolbarContainer>
)

const NoRowsOverlay = () => (
    <Stack sx={{ justifyContent: 'center', height: '100%', alignItems: 'center', color: '#0F172B' }}>
        No Data Found
    </Stack>
)

// FIX: the ~150-line sx block was duplicated across both grids. Single source now.
const dataGridSx = {
    borderRadius: '5px',
    overflow: 'hidden',
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#F2F6F9',
        color: '#45556C',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'uppercase',
        border: 'none',
        minHeight: '54px !important',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'start !important',
    },
    '& .MuiDataGrid-columnHeader': {
        padding: '0 16px',
        outline: 'none !important',
        justifyContent: 'flex-start',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 600,
        textAlign: 'left',
    },
    '& .MuiDataGrid-main': {
        backgroundColor: '#ffffff',
        borderRadius: '5px 5px 0px 0px',
        borderTop: '1px solid #E2E8F0',
        borderLeft: '1px solid #E2E8F0',
        borderRight: '1px solid #E2E8F0',
        borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
        backgroundColor: '#ffffff',
        color: '#0F172B',
        borderBottom: '1px solid #E2E8F0 !important',
        '&:hover': {
            backgroundColor: '#f3f4f6 !important',
        },
    },
    '& .MuiDataGrid-cell': {
        borderBottom: 'none',
        padding: '0 16px',
        fontSize: '14px !important',
        outline: 'none !important',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none !important',
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none !important',
    },
    '& .MuiDataGrid-cell:hover': {
        cursor: 'pointer',
    },
    '& .MuiDataGrid-columnSeparator': {
        display: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: 'none',
        borderLeft: '1px solid #E2E8F0',
        borderRight: '1px solid #E2E8F0',
        borderBottom: '1px solid #E2E8F0',
        backgroundColor: '#F2F6F9',
        minHeight: '52px',
        padding: '0 16px',
        borderRadius: '0px 0px 5px 5px',
    },
    '& .MuiTablePagination-root': {
        color: '#6b7280',
    },
    '& .MuiTablePagination-actions': {
        color: '#6b7280',
    },
    '& .MuiTablePagination-actions .MuiIconButton-root': {
        color: '#6b7280',
    },
    '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
        color: '#d1d5db',
    },
    '& .MuiTablePagination-displayedRows': {
        color: '#6b7280',
        fontSize: '14px',
    },
    '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': {
        color: '#525252 !important',
    },
    '& .MuiDataGrid-toolbarContainer': {
        margin: '5px !important',
    },
    '& .MuiDataGrid-overlay': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-columnHeader--moving': {
        backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
        marginTop: '18px',
    },
} as const

/* ------------------------------------------------------------------ */
/* Reusable grid wrapper                                               */
/* ------------------------------------------------------------------ */
// FIX: the two DataGrids were ~95% identical. One component now.
interface DashboardGridProps {
    className?: string
    rows: GridRowsProp
    columns: GridColDef[]
    getRowId: (row: any) => string | number
}

const DashboardGrid: React.FC<DashboardGridProps> = ({ className, rows, columns, getRowId }) => (
    <DataGrid
        className={className}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        density="compact"
        hideFooterSelectedRowCount
        rowHeight={60}
        // FIX: pageSize and rowsPerPageOptions now agree (was 10 vs [5]).
        pageSize={10}
        rowsPerPageOptions={[5, 10, 25]}
        components={{
            Pagination: CustomPagination,
            Toolbar: CustomToolbar,
            NoRowsOverlay,
        }}
        autoHeight
        sx={dataGridSx}
    />
)

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

const AdminDashboard = () => {
    const {
        adminDashboardDetails,
        setAdminDashboardDetails,
        setAdminDashboardGlobalDetails,
        adminDashboardFormDetails,
        setAdminDashboardFormDetails,
        removeAgentAPI,
    } = useContext(AdminDashboardContext) as AdminDashboardContextPayload

    const { setFlagLoader } = useContext(UserContext) as UserContextPayload

    const { showSnackbar } = useSnackbar()
    const navigate = useNavigate()

    const [tabNavigationvalue, setTabNavigationvalue] = useState<string>('RecentAgentLogins')

    // FIX: single menu state for the whole grid instead of one Menu per row.
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedAgent, setSelectedAgent] = useState<AgentRow | null>(null)
    const openMenu = Boolean(menuAnchorEl)

    const [isEditMode, setIsEditMode] = useState(false)
    const [flagOpenEditAgentPopup, setFlagOpenEditAgentPopup] = useState(false)
    const [flagOpenAgentDeleteDialog, setFlagOpenAgentDeleteDialog] = useState(false)

    /* ---------------------------------------------------------------- */
    /* Data fetching                                                    */
    /* ---------------------------------------------------------------- */

    const getAdminDashboardDetailsFunction = useCallback(async () => {
        setFlagLoader(true)
        try {
            const response = await agent.getAdminDashboardDetailsAPI.get()
            if (response?.status && response?.data) {
                const mappedData: AdminDetailsResponse = {
                    overall: response?.data?.overall,
                    recentAgentLoginDetails: response?.data?.list_of_active_agents,
                    profileDetails: response?.data?.list_of_profiles,
                }
                setAdminDashboardDetails(mappedData)
                setAdminDashboardGlobalDetails(mappedData)
            } else {
                const errorMessage = response?.data?.error?.message || 'Something Went Wrong!'
                showSnackbar(errorMessage, 'error')
            }
            return response
        } catch (error) {
            console.error('Error fetching admin dashboard details:', error)
            showSnackbar('Something Went Wrong!', 'error')
        } finally {
            setFlagLoader(false)
        }
    }, [setFlagLoader, setAdminDashboardDetails, setAdminDashboardGlobalDetails, showSnackbar])

    // Mount-only fetch. Do not depend on getAdminDashboardDetailsFunction —
    // that callback historically recreated when showSnackbar identity changed
    // after an error toast, which re-fired this effect in a loop.
    useEffect(() => {
        getAdminDashboardDetailsFunction()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // FIX: derive counts instead of mirroring them into state with an effect.
    const numberOfCounts = useMemo(() => {
        const overall = adminDashboardDetails?.overall
        return {
            totalAgents: overall?.total_agents ?? 0,
            totalProfile: overall?.total_profiles ?? 0,
            totalActiveAgents: overall?.active_agents ?? 0,
            totalPendingRequests: overall?.pending_requests_count ?? 0,
        }
    }, [adminDashboardDetails])

    /* ---------------------------------------------------------------- */
    /* Menu + row actions                                               */
    /* ---------------------------------------------------------------- */

    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, row: AgentRow) => {
        setMenuAnchorEl(e.currentTarget)
        setSelectedAgent(row)
    }

    const handleCloseMenu = () => {
        setMenuAnchorEl(null)
    }

    const handleOpenEditFromMenu = () => {
        if (!selectedAgent) return
        setAdminDashboardFormDetails({
            first_name: selectedAgent.firstname,
            last_name: selectedAgent.lastname,
            email_address: selectedAgent.email,
            contact_number: selectedAgent.phone_number,
        })
        setIsEditMode(true)
        setFlagOpenEditAgentPopup(true)
        setMenuAnchorEl(null)
    }

    const handleOpenDeleteFromMenu = () => {
        if (!selectedAgent) return
        setFlagOpenAgentDeleteDialog(true)
        setMenuAnchorEl(null)
    }

    // FIX: status toggle now refreshes data so the switch reflects real state
    // and doesn't snap back. Errors are surfaced; success refetches.
    const ChangeStatus = async (e: React.ChangeEvent<HTMLInputElement>, row: AgentRow) => {
        try {
            setFlagLoader(true)
            const changeStatusPayload = {
                agent_id: row?.user_id,
                agent_active_flag: e.target.checked,
            }
            const response = await agent.inActiveAgentAPI.put(changeStatusPayload)
            if (response?.status) {
                showSnackbar('Status Updated Successfully!', 'success')
                await getAdminDashboardDetailsFunction()
            } else {
                showSnackbar('Failed to update agent status!', 'error')
            }
        } catch (error) {
            console.error('Error updating agent status:', error)
            showSnackbar('Failed to update agent status!', 'error')
        } finally {
            setFlagLoader(false)
        }
    }

    const handleEditSubmit = async (agentData: AgentFormDetails) => {
        if (!selectedAgent) return
        try {
            setFlagLoader(true)
            const updateData = {
                // FIX: use the selected row's id directly instead of an
                // overloaded "idOfAgentToBeRemoved" state var.
                agent_id: selectedAgent.user_id,
                firstname: agentData.first_name,
                lastname: agentData.last_name,
                email: agentData.email_address,
                contact: agentData.contact_number,
            }
            const response = await agent.updateAgentRegisterAPI.put(updateData)
            if (response?.status) {
                await getAdminDashboardDetailsFunction()
                showSnackbar('Agent updated successfully!', 'success')
                setFlagOpenEditAgentPopup(false)
            } else {
                showSnackbar('Failed to update agent!', 'error')
            }
        } catch (error) {
            console.error('Error updating agent:', error)
            showSnackbar('Failed to update agent!', 'error')
        } finally {
            setFlagLoader(false)
            setAdminDashboardFormDetails({ ...EMPTY_FORM })
        }
    }

    const handleRemoveAgent = async () => {
        if (!selectedAgent) return
        try {
            setFlagLoader(true)
            const response = await removeAgentAPI(selectedAgent.user_id)
            if (response?.status) {
                await getAdminDashboardDetailsFunction()
                showSnackbar('Agent Deleted Successfully!', 'success')
            } else {
                const errorMessage = response?.data?.error?.message || 'Something Went Wrong!'
                showSnackbar(errorMessage, 'error')
            }
        } catch (error) {
            console.error('Error deleting agent:', error)
            showSnackbar('Something went wrong!', 'error')
        } finally {
            setFlagOpenAgentDeleteDialog(false)
            setFlagLoader(false)
            setAdminDashboardFormDetails({ ...EMPTY_FORM })
        }
    }

    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        setTabNavigationvalue(newValue)
    }

    /* ---------------------------------------------------------------- */
    /* Columns                                                          */
    /* ---------------------------------------------------------------- */

    const recentAgentLoginColumns: GridColDef[] = [
        {
            field: 'firstname',
            headerName: 'FIRST NAME',
            align: 'left',
            headerAlign: 'left',
            width: 200,
            renderCell: (params) => {
                const userRole: string = params.row.user_role
                const firstName: string = params.row.firstname
                return (
                    <Grid item>
                        <Grid container>
                            {firstName}&nbsp;&nbsp;
                            {userRole === 'Admin' && <Box className={`${styles.adminlabel}`}>{userRole}</Box>}
                        </Grid>
                    </Grid>
                )
            },
        },
        {
            field: 'lastname',
            headerName: 'LAST NAME',
            align: 'left',
            headerAlign: 'left',
            width: 200,
        },
        {
            field: 'last_login',
            headerName: 'LAST LOGIN',
            valueGetter: (params: GridValueGetterParams) =>
                params?.row?.last_login ? moment(params?.row?.last_login)?.format('MM/DD/YYYY  hh:mm A') : '',
            align: 'left',
            headerAlign: 'left',
            width: 200,
        },
        {
            field: 'email',
            headerName: 'EMAIL',
            align: 'left',
            headerAlign: 'left',
            width: 300,
        },
        {
            field: 'phone_number',
            headerName: 'CONTACT',
            align: 'left',
            headerAlign: 'left',
            width: 200,
        },
        {
            field: 'profile_count',
            headerName: 'PROFILES',
            align: 'left',
            headerAlign: 'left',
            width: 150,
        },
        {
            field: 'status',
            headerName: 'STATUS',
            align: 'left',
            headerAlign: 'left',
            width: 140,
            renderCell: (params) => {
                const rowData = params.row as AgentRow
                const isActive = rowData.status === 'Active'
                if (rowData?.user_role?.toLowerCase() === 'admin') return null
                return (
                    <FormGroup className={`${styles.agentStatusSwitch}`}>
                        <FormControlLabel
                            label=""
                            sx={{
                                '& .MuiFormControlLabel-root ': {
                                    marginLeft: '0px !important',
                                },
                            }}
                            control={
                                <IOSSwitch
                                    checked={isActive}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => ChangeStatus(e, rowData)}
                                />
                            }
                        />
                    </FormGroup>
                )
            },
        },
        {
            field: 'actions',
            headerName: '',
            align: 'left',
            headerAlign: 'left',
            sortable: false,
            disableColumnMenu: true,
            width: 90,
            renderCell: (params) => {
                const row = params.row as AgentRow
                if (row?.user_role?.toLowerCase() === 'admin') return null
                return (
                    <Button
                        aria-label="row actions"
                        aria-haspopup="true"
                        onClick={(e) => handleOpenMenu(e, row)}
                    >
                        <MoreVertIcon
                            sx={{
                                color: '#666666 !important',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    borderRadius: '5px',
                                },
                            }}
                        />
                    </Button>
                )
            },
        },
    ]

    const latestCreatedProfilesColumns: GridColDef[] = [
        {
            field: 'user_name',
            headerName: 'NAME',
            align: 'left',
            headerAlign: 'left',
            minWidth: 285,
        },
        {
            field: 'user_profile_type_id',
            headerName: 'PROFILE TYPE',
            align: 'left',
            headerAlign: 'left',
            minWidth: 250,
            renderCell: (params) => {
                const profileType: number = params?.row?.user_profile_type_id
                // FIX: always render a string (was assigning a raw number to a
                // string-typed variable on the default branch).
                const displayType = PROFILE_TYPE_LABELS[profileType] ?? String(profileType ?? '')
                return <span>{displayType}</span>
            },
        },
        {
            field: 'change_log_timestamp',
            headerName: 'CREATED DATE',
            valueGetter: (params: GridValueGetterParams) =>
                params?.row?.change_log_timestamp
                    ? moment(params?.row?.change_log_timestamp)?.format('MM/DD/YYYY  hh:mm A')
                    : '',
            align: 'left',
            headerAlign: 'left',
            minWidth: 250,
        },
        {
            field: 'user_email_address',
            headerName: 'EMAIL',
            align: 'left',
            headerAlign: 'left',
            minWidth: 320,
        },
        {
            field: 'user_contact',
            headerName: 'CONTACT',
            align: 'left',
            headerAlign: 'left',
            minWidth: 250,
        },
        {
            field: 'profile_count',
            headerName: 'ASSIGNEE',
            align: 'left',
            headerAlign: 'left',
            minWidth: 200,
            renderCell: (params) => {
                const isSampleProfile: boolean = params?.row?.sample_profile_flag
                const assigneeName: string | null = params?.row?.assignee_fullname
                return isSampleProfile ? (
                    <span
                        style={{
                            fontSize: '13px',
                            color: '#17D600',
                            border: '1px solid #17D600',
                            borderRadius: '5px',
                            padding: '2px 10px',
                        }}
                    >
                        Sample Profile
                    </span>
                ) : (
                    <span
                        style={{
                            fontSize: '13px',
                            color: 'black',
                            borderRadius: '5px',
                            padding: '2px 10px',
                        }}
                    >
                        {assigneeName ?? ''}
                    </span>
                )
            },
        },
    ]

    const chipDetails = [
        { name: '# Agents', number: numberOfCounts.totalAgents, img: company, flag: false },
        { name: '# Profiles', number: numberOfCounts.totalProfile, img: profiles, flag: false },
        { name: '# Active Agents (Last 30 days)', number: numberOfCounts.totalActiveAgents, img: activeAgent, flag: true },
        { name: 'Pending Requests', number: numberOfCounts.totalPendingRequests, img: pendingReqIcon, flag: true, path: '/pending-requests' },
    ]

    /* ---------------------------------------------------------------- */
    /* Render                                                           */
    /* ---------------------------------------------------------------- */

    // FIX: never pass undefined to DataGrid.rows.
    const agentRows = adminDashboardDetails?.recentAgentLoginDetails ?? []
    const profileRows = adminDashboardDetails?.profileDetails ?? []

    return (
        <>
            {flagOpenEditAgentPopup && (
                <GlobalPopUp
                    open={flagOpenEditAgentPopup}
                    onClose={() => setFlagOpenEditAgentPopup(false)}
                    title="Edit Agent"
                    showCloseIcon
                    flagDivider
                    maxWidth="sm"
                >
                    <RegisterAgent
                        agentFormDetails={adminDashboardFormDetails}
                        setagentFormDetails={setAdminDashboardFormDetails}
                        setFlagOpenAgentDetailsDialog={setFlagOpenEditAgentPopup}
                        handleSubmit={handleEditSubmit}
                        isEditMode={isEditMode}
                        selectedAgent={selectedAgent}
                    />
                </GlobalPopUp>
            )}

            {flagOpenAgentDeleteDialog && (
                <GlobalPopUp
                    open={flagOpenAgentDeleteDialog}
                    onClose={() => setFlagOpenAgentDeleteDialog(false)}
                    title="Delete Agent"
                    showCloseIcon
                    flagDivider
                    maxWidth="sm"
                >
                    <GlobalDeletePopup
                        popupDescription={`Are you sure you want to delete agent ${selectedAgent?.firstname ?? ''}?`}
                        handleClose={() => setFlagOpenAgentDeleteDialog(false)}
                        onDelete={handleRemoveAgent}
                    />
                </GlobalPopUp>
            )}

            {/* FIX: a single Menu for the whole grid, rendered once. */}
            <Menu
                id="row-action-menu"
                MenuListProps={{ 'aria-labelledby': 'row actions' }}
                anchorEl={menuAnchorEl}
                open={openMenu}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    style: {
                        boxShadow: '0px 0px 1px 0px #00000033',
                        background: '#FFFFFF',
                    },
                }}
            >
                <MenuItem
                    sx={{ color: '#000000', '&:hover': { backgroundColor: 'white', color: '#105EA7' } }}
                    onClick={handleOpenEditFromMenu}
                >
                    <ModeEditOutlineOutlinedIcon fontSize="small" />&nbsp;<span>Edit</span>
                </MenuItem>
                <MenuItem
                    sx={{ color: '#000000', '&:hover': { backgroundColor: 'white', color: '#105EA7' } }}
                    onClick={handleOpenDeleteFromMenu}
                >
                    <DeleteForeverOutlinedIcon fontSize="small" />&nbsp;<span>Delete</span>
                </MenuItem>
            </Menu>

            <Grid container>
                <Grid item xs={12} className={`${styles.adminProfileMainItem}`}>
                    <Grid container>
                        <Grid item xs={12} className={styles.globalBlurWrapper}>
                            <Grid container gap={3}>
                                <Grid item xs={12}>
                                    <Grid container className={styles.chipContainerWrapper}>
                                        <Grid item xs={12}>
                                            <Grid container className={styles.chipContainerMain}>
                                                {chipDetails?.map((ele) => {
                                                    const chipPath = 'path' in ele ? ele.path : undefined
                                                    const chipSizeClass = ele.flag ? styles.chipItemLarge : styles.chipItem
                                                    const chipClassName = [
                                                        styles.chipCommanStyle,
                                                        chipSizeClass,
                                                        chipPath ? styles.chipItemInteractive : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')
                                                    return (
                                                        <React.Fragment key={ele.name}>
                                                            <Grid
                                                                item
                                                                xs={3}
                                                                sm={2}
                                                                md={2}
                                                                lg={4}
                                                                xl={4}
                                                                className={chipClassName}
                                                                onClick={chipPath ? () => navigate(chipPath) : undefined}
                                                                onKeyDown={
                                                                    chipPath
                                                                        ? (e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') navigate(chipPath)
                                                                        }
                                                                        : undefined
                                                                }
                                                                role={chipPath ? 'button' : undefined}
                                                                tabIndex={chipPath ? 0 : undefined}
                                                                sx={chipPath ? { cursor: 'pointer' } : undefined}
                                                            >
                                                                <Grid container sx={{ height: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Grid item className={styles.chipLogoContainer}>
                                                                        <img src={ele.img} alt="" />
                                                                    </Grid>
                                                                    <Grid item className={styles.chipContentItem}>
                                                                        <Grid container>
                                                                            <Grid item xs={12} className={styles.chipHeadItem}>
                                                                                <Typography component="span">{ele.name}</Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} className={styles.chipSubheadItem}>
                                                                                <Typography component="span">{ele.number}</Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                    )
                                                })}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12} className={`${styles.adminDashboardTabMainWrapper}`}>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <div className={styles.tabsWrapper}>
                                                        <GlobalTabsComponent
                                                            value={tabNavigationvalue}
                                                            onChange={handleChange}
                                                            normalColor="#45556C"
                                                            selectedColor="#04385D"
                                                            indicatorColor="var(--secondary-color)"
                                                            tabs={[
                                                                { label: 'Recent Agent Logins', value: 'RecentAgentLogins' },
                                                                { label: 'Latest Created Profiles', value: 'LatestCreatedProfiles' },
                                                            ]}
                                                        />
                                                    </div>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    {tabNavigationvalue === 'RecentAgentLogins' && (
                                                        <DashboardGrid
                                                            className={`${styles.adminDashboardTable}`}
                                                            rows={agentRows}
                                                            columns={recentAgentLoginColumns}
                                                            getRowId={(row: AgentRow) => row.user_id}
                                                        />
                                                    )}
                                                    {tabNavigationvalue === 'LatestCreatedProfiles' && (
                                                        <DashboardGrid
                                                            className={`${styles.latestCreatedProfileTable}`}
                                                            rows={profileRows}
                                                            columns={latestCreatedProfilesColumns}
                                                            getRowId={(row: ProfileRow) => row.user_profile_id}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default AdminDashboard